import React, { useState, useEffect } from 'react';
import {
    string,
    func,
    objectOf,
    oneOfType,
    arrayOf,
    number,
    bool,
    shape,
} from 'prop-types';
import classnames from 'classnames';
import compose from 'lodash/flowRight';
import { promotedFiltersShape } from 'shapes';
import { withGTM } from 'components';
import { connectMultipleFilters } from 'connectors';
import Pastille from '../Pastille/Pastille';
import { getOtherPastilleAttributes } from '../../utils/guidedNavigation';
import { isPastilleActive } from './utils';

function PromotedFiltersStep({
    className,
    promotedFilters,
    pushToGTM,
    currentRefinements,
    onSelectPastille,
    refine,
}) {
    const rootClassnames = classnames('promoted-filters-step', className);
    const activePastille = promotedFilters.pastilles.find(pastille =>
        isPastilleActive(pastille, currentRefinements),
    );
    const [activePastilleId, setActivePastilleId] = useState(
        activePastille ? activePastille.id : null,
    );
    const [prevPromotedFiltersId, setPrevPromotedFiltersId] = useState(null);

    useEffect(() => {
        if (activePastilleId && prevPromotedFiltersId === promotedFilters.id) {
            const pastille = promotedFilters.pastilles.find(
                ({ id }) => id === activePastilleId,
            );
            refine(
                pastille.attributes,
                getOtherPastilleAttributes(promotedFilters, pastille),
            );
            onSelectPastille();
        }

        if (prevPromotedFiltersId !== promotedFilters.id) {
            setPrevPromotedFiltersId(promotedFilters.id);
        }
    }, [activePastilleId]);

    const handleSelectPastille = (evt, pastille) => {
        pushToGTM({
            event: 'filter_results',
            event_trigger: 'promoted_filters',
            filter_id:
                pastille.attributes &&
                Object.values(pastille.attributes).map(value =>
                    value.key.match('catalog_attribute.')
                        ? value.key.replace('catalog_attribute.', 'cat_attr_')
                        : value.key,
                ),
            filter_name: promotedFilters.name,
            filter_position: pastille.position,
            filter_type:
                pastille.attributes &&
                Object.values(pastille.attributes).map(value => value.type),
            filter_value: pastille.name,
        });

        if (activePastille && pastille.id === activePastille.id) {
            onSelectPastille(true);
        }
        setActivePastilleId(pastille.id);
    };

    return (
        <div className={rootClassnames}>
            <h1 className="promoted-filters-step__title">
                <span>{promotedFilters.title}</span>
            </h1>
            <div className="promoted-filters-step__pastilles">
                {promotedFilters.pastilles &&
                    promotedFilters.pastilles.map(pastille => (
                        <Pastille
                            key={pastille.id}
                            onSelectPastille={handleSelectPastille}
                            active={pastille.id === activePastilleId}
                            {...pastille}
                        />
                    ))}
            </div>
        </div>
    );
}

PromotedFiltersStep.propTypes = {
    className: string,
    promotedFilters: promotedFiltersShape.isRequired,
    pushToGTM: func.isRequired,
    refine: func.isRequired,
    onSelectPastille: func,
    currentRefinements: objectOf(
        oneOfType([
            string,
            arrayOf(string),
            number,
            bool,
            shape({
                min: number,
                max: number,
            }),
        ]),
    ).isRequired,
};

PromotedFiltersStep.defaultProps = {
    className: null,
    onSelectPastille: null,
};

export default compose(
    connectMultipleFilters,
    withGTM,
)(PromotedFiltersStep);
